import { FC, useState } from 'react';
import { MarkdownEditor } from 'src/shared/MarkdownEditor';
import {
    CommandLine,
    DiffBlock,
    DiffNew,
    DiffOld,
    FilePath,
    MonoBlock,
    RawJson,
    ResultError,
    ResultLabel,
    ToolBody,
    ToolCard as ToolCardWrap,
    ToolChevron,
    ToolHeader,
    ToolIcon,
    ToolKind,
    ToolName,
    ToolSection,
    ToolSectionLabel,
    ToolSummary,
    TodoItem,
    TodoList
} from '../styled';

interface Props {
    name: string;
    input: unknown;
    result: unknown;
    isError: boolean;
}

const isObject = (v: unknown): v is Record<string, unknown> =>
    typeof v === 'object' && v !== null && !Array.isArray(v);

const EXT_TO_LANG: Record<string, string> = {
    ts: 'typescript',
    tsx: 'tsx',
    js: 'javascript',
    jsx: 'jsx',
    py: 'python',
    rb: 'ruby',
    go: 'go',
    rs: 'rust',
    java: 'java',
    kt: 'kotlin',
    swift: 'swift',
    c: 'c',
    cpp: 'cpp',
    h: 'c',
    cs: 'csharp',
    sh: 'bash',
    bash: 'bash',
    zsh: 'bash',
    yml: 'yaml',
    yaml: 'yaml',
    json: 'json',
    toml: 'toml',
    xml: 'xml',
    html: 'html',
    css: 'css',
    scss: 'scss',
    sql: 'sql',
    md: 'markdown',
    dockerfile: 'dockerfile',
    tf: 'hcl',
    graphql: 'graphql',
    proto: 'protobuf'
};

const langFromPath = (filePath: string): string => {
    const name = filePath.split('/').pop()?.toLowerCase() ?? '';
    if (name === 'dockerfile') return 'dockerfile';
    const ext = name.split('.').pop() ?? '';
    return EXT_TO_LANG[ext] || 'text';
};

const isMarkdown = (filePath: string): boolean => {
    const ext = filePath.split('.').pop()?.toLowerCase() ?? '';
    return ext === 'md' || ext === 'mdx';
};

const toCodeFencedMarkdown = (code: string, language: string): string =>
    `\`\`\`${language}\n${code}\n\`\`\``;

/* ──────────────────────────── Tool taxonomy ──────────────────────────── */

const TOOL_META: Record<
    string,
    { kind: ToolKind; icon: string; aliases?: string[] }
> = {
    Bash: { kind: 'bash', icon: '$' },
    Read: { kind: 'read', icon: 'R' },
    Edit: { kind: 'edit', icon: '✎' },
    MultiEdit: { kind: 'edit', icon: '✎' },
    Write: { kind: 'write', icon: '+' },
    NotebookEdit: { kind: 'edit', icon: '✎' },
    Grep: { kind: 'grep', icon: '⌕' },
    Glob: { kind: 'glob', icon: '✱' },
    WebFetch: { kind: 'web-fetch', icon: '↗' },
    WebSearch: { kind: 'web-search', icon: '⌕' },
    Agent: { kind: 'agent', icon: 'A' },
    Task: { kind: 'agent', icon: 'A' },
    TodoWrite: { kind: 'todo', icon: '☑' }
};

const metaFor = (name: string) => {
    if (TOOL_META[name]) return TOOL_META[name];
    return { kind: 'generic' as ToolKind, icon: '⚙' };
};

const truncate = (s: string, n = 80) =>
    s.length > n ? s.slice(0, n - 1) + '…' : s;

const summaryOf = (name: string, input: unknown): string => {
    if (!isObject(input)) return '';
    const s = (k: string) =>
        typeof input[k] === 'string' ? (input[k] as string) : '';
    switch (name) {
        case 'Bash':
            return truncate(s('command'));
        case 'Read':
            return s('file_path');
        case 'Edit':
        case 'MultiEdit':
            return s('file_path');
        case 'Write':
            return s('file_path');
        case 'Grep':
            return truncate(
                `${s('pattern')} ${s('path') ? `in ${s('path')}` : ''}`
            );
        case 'Glob':
            return s('pattern');
        case 'WebFetch':
            return s('url');
        case 'WebSearch':
            return s('query');
        case 'Agent':
        case 'Task':
            return s('description') || s('subagent_type');
        case 'TodoWrite':
            return Array.isArray(input.todos)
                ? `${input.todos.length} item${input.todos.length === 1 ? '' : 's'}`
                : '';
        default:
            return '';
    }
};

const renderResult = (result: unknown): string => {
    if (result == null) return '';
    if (typeof result === 'string') return result;
    if (Array.isArray(result)) {
        return result
            .map((b) => {
                if (typeof b === 'string') return b;
                if (isObject(b) && typeof b.text === 'string') return b.text;
                return JSON.stringify(b);
            })
            .join('\n');
    }
    return JSON.stringify(result, null, 2);
};

/* ──────────────────────────── Per-tool bodies ──────────────────────────── */

const BashBody: FC<Props> = ({ input, result, isError }) => {
    const cmd =
        isObject(input) && typeof input.command === 'string'
            ? input.command
            : '';
    const desc =
        isObject(input) && typeof input.description === 'string'
            ? input.description
            : '';
    return (
        <ToolBody>
            {desc && (
                <ToolSection>
                    <ToolSectionLabel>Description</ToolSectionLabel>
                    <span>{desc}</span>
                </ToolSection>
            )}
            <ToolSection>
                <ToolSectionLabel>Command</ToolSectionLabel>
                <CommandLine>{cmd}</CommandLine>
            </ToolSection>
            {result != null && (
                <ToolSection>
                    <ResultLabel>
                        Output
                        {isError && <ResultError>error</ResultError>}
                    </ResultLabel>
                    <MonoBlock>{renderResult(result)}</MonoBlock>
                </ToolSection>
            )}
        </ToolBody>
    );
};

const ReadBody: FC<Props> = ({ input, result, isError }) => {
    const fp =
        isObject(input) && typeof input.file_path === 'string'
            ? input.file_path
            : '';
    const offset =
        isObject(input) && typeof input.offset === 'number'
            ? (input.offset as number)
            : null;
    const limit =
        isObject(input) && typeof input.limit === 'number'
            ? (input.limit as number)
            : null;
    const lang = langFromPath(fp);
    return (
        <ToolBody>
            <ToolSection>
                <ToolSectionLabel>File</ToolSectionLabel>
                <FilePath>{fp}</FilePath>
                {(offset !== null || limit !== null) && (
                    <ToolSectionLabel>
                        lines {offset ?? 1}
                        {limit ? ` (${limit} lines)` : ''}
                    </ToolSectionLabel>
                )}
            </ToolSection>
            {result != null && (
                <ToolSection>
                    <ResultLabel>
                        Content
                        {isError && <ResultError>error</ResultError>}
                    </ResultLabel>
                    <MarkdownEditor
                        value={
                            isMarkdown(fp)
                                ? renderResult(result)
                                : toCodeFencedMarkdown(
                                      renderResult(result),
                                      lang
                                  )
                        }
                        readOnly
                        inline
                    />
                </ToolSection>
            )}
        </ToolBody>
    );
};

const EditBody: FC<Props> = ({ input, result, isError }) => {
    const fp =
        isObject(input) && typeof input.file_path === 'string'
            ? input.file_path
            : '';
    const oldStr =
        isObject(input) && typeof input.old_string === 'string'
            ? (input.old_string as string)
            : '';
    const newStr =
        isObject(input) && typeof input.new_string === 'string'
            ? (input.new_string as string)
            : '';
    const edits =
        isObject(input) && Array.isArray(input.edits)
            ? (input.edits as Array<Record<string, unknown>>)
            : null;
    return (
        <ToolBody>
            <ToolSection>
                <ToolSectionLabel>File</ToolSectionLabel>
                <FilePath>{fp}</FilePath>
            </ToolSection>
            {edits ? (
                edits.map((e, i) => (
                    <ToolSection key={i}>
                        <ToolSectionLabel>Edit {i + 1}</ToolSectionLabel>
                        <DiffBlock>
                            {typeof e.old_string === 'string' &&
                                e.old_string
                                    .split('\n')
                                    .map((l, li) => (
                                        <DiffOld key={`o-${li}`}>{l}</DiffOld>
                                    ))}
                            {typeof e.new_string === 'string' &&
                                e.new_string
                                    .split('\n')
                                    .map((l, li) => (
                                        <DiffNew key={`n-${li}`}>{l}</DiffNew>
                                    ))}
                        </DiffBlock>
                    </ToolSection>
                ))
            ) : (
                <ToolSection>
                    <ToolSectionLabel>Diff</ToolSectionLabel>
                    <DiffBlock>
                        {oldStr.split('\n').map((l, i) => (
                            <DiffOld key={`o-${i}`}>{l}</DiffOld>
                        ))}
                        {newStr.split('\n').map((l, i) => (
                            <DiffNew key={`n-${i}`}>{l}</DiffNew>
                        ))}
                    </DiffBlock>
                </ToolSection>
            )}
            {result != null && (
                <ToolSection>
                    <ResultLabel>
                        Result
                        {isError && <ResultError>error</ResultError>}
                    </ResultLabel>
                    <MonoBlock>{renderResult(result)}</MonoBlock>
                </ToolSection>
            )}
        </ToolBody>
    );
};

const WriteBody: FC<Props> = ({ input, result, isError }) => {
    const fp =
        isObject(input) && typeof input.file_path === 'string'
            ? input.file_path
            : '';
    const content =
        isObject(input) && typeof input.content === 'string'
            ? (input.content as string)
            : '';
    const lang = langFromPath(fp);
    return (
        <ToolBody>
            <ToolSection>
                <ToolSectionLabel>File</ToolSectionLabel>
                <FilePath>{fp}</FilePath>
            </ToolSection>
            <ToolSection>
                <ToolSectionLabel>Content</ToolSectionLabel>
                <MarkdownEditor
                    value={
                        isMarkdown(fp)
                            ? content
                            : toCodeFencedMarkdown(content, lang)
                    }
                    readOnly
                    inline
                />
            </ToolSection>
            {result != null && (
                <ToolSection>
                    <ResultLabel>
                        Result
                        {isError && <ResultError>error</ResultError>}
                    </ResultLabel>
                    <MonoBlock>{renderResult(result)}</MonoBlock>
                </ToolSection>
            )}
        </ToolBody>
    );
};

const GrepBody: FC<Props> = ({ input, result, isError }) => {
    const pattern =
        isObject(input) && typeof input.pattern === 'string'
            ? input.pattern
            : '';
    const path =
        isObject(input) && typeof input.path === 'string' ? input.path : '';
    const glob =
        isObject(input) && typeof input.glob === 'string' ? input.glob : '';
    return (
        <ToolBody>
            <ToolSection>
                <ToolSectionLabel>Pattern</ToolSectionLabel>
                <FilePath>{pattern}</FilePath>
            </ToolSection>
            {(path || glob) && (
                <ToolSection>
                    <ToolSectionLabel>Scope</ToolSectionLabel>
                    {path && <FilePath>{path}</FilePath>}
                    {glob && <FilePath>{glob}</FilePath>}
                </ToolSection>
            )}
            {result != null && (
                <ToolSection>
                    <ResultLabel>
                        Matches
                        {isError && <ResultError>error</ResultError>}
                    </ResultLabel>
                    <MonoBlock>{renderResult(result)}</MonoBlock>
                </ToolSection>
            )}
        </ToolBody>
    );
};

const GlobBody: FC<Props> = ({ input, result, isError }) => {
    const pattern =
        isObject(input) && typeof input.pattern === 'string'
            ? input.pattern
            : '';
    const path =
        isObject(input) && typeof input.path === 'string' ? input.path : '';
    return (
        <ToolBody>
            <ToolSection>
                <ToolSectionLabel>Pattern</ToolSectionLabel>
                <FilePath>{pattern}</FilePath>
            </ToolSection>
            {path && (
                <ToolSection>
                    <ToolSectionLabel>Path</ToolSectionLabel>
                    <FilePath>{path}</FilePath>
                </ToolSection>
            )}
            {result != null && (
                <ToolSection>
                    <ResultLabel>
                        Files
                        {isError && <ResultError>error</ResultError>}
                    </ResultLabel>
                    <MonoBlock>{renderResult(result)}</MonoBlock>
                </ToolSection>
            )}
        </ToolBody>
    );
};

const WebBody: FC<Props> = ({ input, result, isError, name }) => {
    const url =
        isObject(input) && typeof input.url === 'string' ? input.url : '';
    const query =
        isObject(input) && typeof input.query === 'string' ? input.query : '';
    const prompt =
        isObject(input) && typeof input.prompt === 'string' ? input.prompt : '';
    return (
        <ToolBody>
            {name === 'WebFetch' ? (
                <ToolSection>
                    <ToolSectionLabel>URL</ToolSectionLabel>
                    <FilePath>{url}</FilePath>
                </ToolSection>
            ) : (
                <ToolSection>
                    <ToolSectionLabel>Query</ToolSectionLabel>
                    <FilePath>{query}</FilePath>
                </ToolSection>
            )}
            {prompt && (
                <ToolSection>
                    <ToolSectionLabel>Prompt</ToolSectionLabel>
                    <MonoBlock>{prompt}</MonoBlock>
                </ToolSection>
            )}
            {result != null && (
                <ToolSection>
                    <ResultLabel>
                        Response
                        {isError && <ResultError>error</ResultError>}
                    </ResultLabel>
                    <MonoBlock>{renderResult(result)}</MonoBlock>
                </ToolSection>
            )}
        </ToolBody>
    );
};

const AgentBody: FC<Props> = ({ input, result, isError }) => {
    const desc =
        isObject(input) && typeof input.description === 'string'
            ? input.description
            : '';
    const subagent =
        isObject(input) && typeof input.subagent_type === 'string'
            ? input.subagent_type
            : '';
    const prompt =
        isObject(input) && typeof input.prompt === 'string' ? input.prompt : '';
    return (
        <ToolBody>
            {desc && (
                <ToolSection>
                    <ToolSectionLabel>Description</ToolSectionLabel>
                    <span>{desc}</span>
                </ToolSection>
            )}
            {subagent && (
                <ToolSection>
                    <ToolSectionLabel>Subagent</ToolSectionLabel>
                    <FilePath>{subagent}</FilePath>
                </ToolSection>
            )}
            {prompt && (
                <ToolSection>
                    <ToolSectionLabel>Prompt</ToolSectionLabel>
                    <MonoBlock>{prompt}</MonoBlock>
                </ToolSection>
            )}
            {result != null && (
                <ToolSection>
                    <ResultLabel>
                        Result
                        {isError && <ResultError>error</ResultError>}
                    </ResultLabel>
                    <MonoBlock>{renderResult(result)}</MonoBlock>
                </ToolSection>
            )}
        </ToolBody>
    );
};

interface Todo {
    content?: string;
    status?: string;
    activeForm?: string;
}

const TodoBody: FC<Props> = ({ input }) => {
    const todos =
        isObject(input) && Array.isArray(input.todos)
            ? (input.todos as Todo[])
            : [];
    return (
        <ToolBody>
            <TodoList>
                {todos.map((t, i) => {
                    const status =
                        t.status === 'completed' ||
                        t.status === 'in_progress' ||
                        t.status === 'pending'
                            ? t.status
                            : 'unknown';
                    return (
                        <TodoItem key={i} status={status}>
                            {status === 'in_progress' && t.activeForm
                                ? t.activeForm
                                : (t.content ?? '(no content)')}
                        </TodoItem>
                    );
                })}
            </TodoList>
        </ToolBody>
    );
};

const GenericBody: FC<Props> = ({ input, result, isError }) => (
    <ToolBody>
        <ToolSection>
            <ToolSectionLabel>Input</ToolSectionLabel>
            <RawJson>{JSON.stringify(input, null, 2)}</RawJson>
        </ToolSection>
        {result != null && (
            <ToolSection>
                <ResultLabel>
                    Output
                    {isError && <ResultError>error</ResultError>}
                </ResultLabel>
                <MonoBlock>{renderResult(result)}</MonoBlock>
            </ToolSection>
        )}
    </ToolBody>
);

const bodyFor = (name: string): FC<Props> => {
    switch (name) {
        case 'Bash':
            return BashBody;
        case 'Read':
            return ReadBody;
        case 'Edit':
        case 'MultiEdit':
        case 'NotebookEdit':
            return EditBody;
        case 'Write':
            return WriteBody;
        case 'Grep':
            return GrepBody;
        case 'Glob':
            return GlobBody;
        case 'WebFetch':
        case 'WebSearch':
            return WebBody;
        case 'Agent':
        case 'Task':
            return AgentBody;
        case 'TodoWrite':
            return TodoBody;
        default:
            return GenericBody;
    }
};

/* ──────────────────────────── Card shell ──────────────────────────── */

const ToolCard: FC<Props> = (props) => {
    const meta = metaFor(props.name);
    // Bash/Edit/Write/Read/Todo are interesting at a glance — keep open by
    // default. Search/Web/Agent often have large content — collapse them.
    const defaultOpen =
        meta.kind === 'bash' ||
        meta.kind === 'edit' ||
        meta.kind === 'write' ||
        meta.kind === 'todo';
    const [open, setOpen] = useState(defaultOpen);
    const Body = bodyFor(props.name);
    const summary = summaryOf(props.name, props.input);

    return (
        <ToolCardWrap kind={meta.kind}>
            <ToolHeader
                kind={meta.kind}
                onClick={() => setOpen((v) => !v)}
                aria-expanded={open}
            >
                <ToolIcon kind={meta.kind}>{meta.icon}</ToolIcon>
                <ToolName>{props.name}</ToolName>
                <ToolSummary>{summary}</ToolSummary>
                {props.isError && <ResultError>error</ResultError>}
                <ToolChevron open={open}>▶</ToolChevron>
            </ToolHeader>
            {open && <Body {...props} />}
        </ToolCardWrap>
    );
};

export default ToolCard;
